import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import api from "../../utilis/api";
import { server } from "../../utilis/constant";

const OrderDetail = () => {
  const { id } = useParams();
  const [orders, setOrders] = useState({});
  const token = useSelector((state) => state.user.token) || "";

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/order/${id}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        setOrders(response.data.data); // note: order, not orders
      } catch (err) {
        console.error(err.response?.data || err);
      }
    };

    if (token) fetchOrder();
  }, [token, id]);

  return (
    <>
      <Helmet>
        <title>My Order Details</title>
        <meta name="description" content="Welcome to My Orders page" />
      </Helmet>

      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-20 py-10 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800">
            Order Details
          </h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Review your purchased items and summary
          </p>
        </div>

        {/* Products Section */}
        <div className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">
          {/* Table Header (desktop only feel) */}
          <div className="hidden sm:grid grid-cols-12 bg-gray-100 px-6 py-4 text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wide">
            <div className="col-span-6">Product</div>
            <div className="col-span-2 text-center">Qty</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2">Total</div>
          </div>

          {/* Items */}
          <div className="divide-y divide-gray-100">
            {orders?.orderItems?.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-1 sm:grid-cols-12 items-center gap-4 px-5 py-5 hover:bg-gray-50 transition"
              >
                {/* Product */}
                <div className="sm:col-span-6 flex items-center gap-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
                    <img
                      src={`${server}/${item?.product?.image}`}
                      alt={item.product?.title}
                      className="object-cover w-full h-full"
                    />
                  </div>

                  <div>
                    <h1 className="font-semibold text-gray-800 text-sm sm:text-base line-clamp-2">
                      {item.product?.title}
                    </h1>
                  </div>
                </div>

                {/* Quantity */}
                <div className="sm:col-span-2 flex sm:justify-center text-gray-700 font-medium">
                  <span className="sm:hidden font-semibold mr-2">Qty:</span>
                  {item.quantity}
                </div>

                {/* Price */}
                <div className="sm:col-span-2 text-gray-700 font-medium">
                  <span className="sm:hidden font-semibold mr-2">Price:</span>$
                  {item.price}
                </div>

                {/* Total */}
                <div className="sm:col-span-2 font-bold text-gray-900">
                  <span className="sm:hidden font-semibold mr-2">Total:</span>$
                  {(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Section */}
        <div className="mt-10 bg-white rounded-3xl shadow-md border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-6">
            Order Summary
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {[
              { label: "Items Price", value: `$${orders?.itemsPrice}` },
              { label: "Tax", value: `$${orders?.taxPrice}` },
              { label: "Shipping Price", value: `$${orders?.shippingPrice}` },
              { label: "Shipping Method", value: orders?.shippingMethod },
              {
                label: "Total Price",
                value: `$${orders?.totalPrice}`,
                highlight: true,
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`flex justify-between items-center p-4 rounded-2xl border transition
              ${item.highlight ? "bg-black text-white" : "bg-gray-50 text-gray-700"}
            `}
              >
                <span className="text-sm sm:text-base font-medium">
                  {item.label}
                </span>
                <span className="font-semibold">{item.value}</span>
              </div>
            ))}

            {/* Payment Status */}
            <div className="flex justify-between items-center p-4 rounded-2xl bg-gray-50 border">
              <span className="font-medium text-gray-700">Payment Status</span>
              <span
                className={`font-semibold ${
                  orders.paymentStatus === "Paid"
                    ? "text-green-600"
                    : "text-yellow-600"
                }`}
              >
                {orders.paymentStatus}
              </span>
            </div>

            {/* Order Status */}
            <div className="flex justify-between items-center p-4 rounded-2xl bg-gray-50 border">
              <span className="font-medium text-gray-700">Order Status</span>
              <span
                className={`font-semibold ${
                  orders.orderStatus === "Delivered"
                    ? "text-green-600"
                    : orders.orderStatus === "Processing"
                      ? "text-blue-600"
                      : "text-red-600"
                }`}
              >
                {orders.orderStatus}
              </span>
            </div>

            {/* Paid */}
            <div className="flex justify-between items-center p-4 rounded-2xl bg-gray-50 border sm:col-span-2">
              <span className="font-medium text-gray-700">Paid</span>
              <span
                className={`font-semibold ${
                  orders.isPaid ? "text-green-600" : "text-red-600"
                }`}
              >
                {orders.isPaid ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetail;
